let dropdownSequence = 0;
const dropdownRegistry = new WeakMap();
const dropdownInstances = new Set();

export function enhanceChartDropdowns(root = document) {
  return [...root.querySelectorAll("select[data-chart-dropdown]")]
    .map((select) => enhanceChartDropdown(select));
}

export function enhanceChartDropdown(select) {
  if (!(select instanceof HTMLSelectElement)) {
    throw new TypeError("A chart dropdown requires a select element.");
  }

  const existing = dropdownRegistry.get(select);
  if (existing) return existing;

  const id = `chart-dropdown-${++dropdownSequence}`;
  const container = document.createElement("div");
  const trigger = document.createElement("button");
  const value = document.createElement("span");
  const menu = document.createElement("div");

  container.className = "chart-dropdown";
  container.dataset.chartDropdownRoot = "";
  const placement = select.dataset.chartDropdownPlacement || "bottom";
  if (placement === "top") container.classList.add("is-top");

  trigger.type = "button";
  trigger.className = "chart-dropdown-trigger";
  trigger.setAttribute("role", "combobox");
  trigger.setAttribute("aria-haspopup", "listbox");
  trigger.setAttribute("aria-expanded", "false");
  trigger.setAttribute("aria-controls", `${id}-menu`);
  copyAccessibleName(select, trigger);

  value.className = "chart-dropdown-value";
  value.dataset.chartDropdownValue = "";
  trigger.append(value, createChevron());

  menu.id = `${id}-menu`;
  menu.className = "chart-dropdown-menu";
  menu.dataset.chartDropdownMenu = "";
  menu.setAttribute("role", "listbox");
  menu.hidden = true;

  select.hidden = true;
  select.tabIndex = -1;
  select.setAttribute("aria-hidden", "true");
  select.insertAdjacentElement("afterend", container);
  container.append(trigger, menu);

  let open = false;
  let optionButtons = [];

  function rebuildOptions() {
    close();
    menu.replaceChildren();
    optionButtons = [...select.options].map((option) => {
      const button = document.createElement("button");
      const content = document.createElement("span");
      const label = document.createElement("span");
      button.type = "button";
      button.className = "chart-dropdown-option";
      button.dataset.value = option.value;
      button.disabled = option.disabled;
      button.setAttribute("role", "option");
      button.setAttribute("aria-disabled", String(option.disabled));
      content.className = "chart-dropdown-option-content";
      label.className = "chart-dropdown-option-label";
      label.textContent = option.textContent;
      const icon = createOptionIcon(option.dataset.icon);
      if (icon) content.append(icon);
      content.append(label);
      button.append(content);
      button.addEventListener("click", () => selectValue(button.dataset.value));
      menu.append(button);
      return button;
    });
    sync();
  }

  function sync() {
    const selected = select.selectedOptions[0] || select.options[0];
    const label = document.createElement("span");
    label.className = "chart-dropdown-value-label";
    label.textContent = selected?.textContent || "";
    const icon = createOptionIcon(selected?.dataset.icon);
    value.replaceChildren(...(icon ? [icon, label] : [label]));
    for (const button of optionButtons) {
      const active = button.dataset.value === select.value;
      button.setAttribute("aria-selected", String(active));
      button.tabIndex = active ? 0 : -1;
    }
  }

  function close({ restoreFocus = false } = {}) {
    if (!open) return;
    open = false;
    menu.hidden = true;
    trigger.setAttribute("aria-expanded", "false");
    container.classList.remove("is-open");
    if (restoreFocus) trigger.focus();
  }

  function openMenu({ focusOption = true } = {}) {
    for (const dropdown of dropdownInstances) {
      if (dropdown.select !== select) dropdown.close();
    }
    open = true;
    menu.hidden = false;
    trigger.setAttribute("aria-expanded", "true");
    container.classList.add("is-open");
    sync();
    positionMenu();
    if (focusOption) {
      optionButtons.find((button) => button.getAttribute("aria-selected") === "true")?.focus();
    }
  }

  function selectValue(nextValue) {
    if (select.value === nextValue) {
      close({ restoreFocus: true });
      return;
    }
    select.value = nextValue;
    sync();
    select.dispatchEvent(new Event("input", { bubbles: true }));
    select.dispatchEvent(new Event("change", { bubbles: true }));
    close({ restoreFocus: true });
  }

  function moveOptionFocus(direction) {
    const current = optionButtons.indexOf(document.activeElement);
    const selected = optionButtons.findIndex(
      (button) => button.getAttribute("aria-selected") === "true"
    );
    const start = current >= 0 ? current : Math.max(0, selected);
    if (optionButtons.length === 0) return;
    let next = direction === "first"
      ? 0
      : direction === "last"
        ? optionButtons.length - 1
        : (start + direction + optionButtons.length) % optionButtons.length;
    for (let attempts = 0; attempts < optionButtons.length; attempts += 1) {
      if (!optionButtons[next]?.disabled) {
        optionButtons[next].focus();
        return;
      }
      next = direction === "last"
        ? (next - 1 + optionButtons.length) % optionButtons.length
        : (next + (direction === -1 ? -1 : 1) + optionButtons.length)
          % optionButtons.length;
    }
  }

  function handleTriggerClick() {
    if (open) close();
    else openMenu();
  }

  function handleTriggerKeydown(event) {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      event.stopPropagation();
      if (!open) openMenu();
      else moveOptionFocus(event.key === "ArrowDown" ? 1 : -1);
    } else if (event.key === "Escape" && open) {
      event.preventDefault();
      event.stopPropagation();
      close({ restoreFocus: true });
    }
  }

  function handleMenuKeydown(event) {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      moveOptionFocus(event.key === "ArrowDown" ? 1 : -1);
    } else if (event.key === "Home" || event.key === "End") {
      event.preventDefault();
      moveOptionFocus(event.key === "Home" ? "first" : "last");
    } else if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      close({ restoreFocus: true });
    } else if (event.key === "Tab") {
      close();
    }
  }

  function handleDocumentPointerDown(event) {
    if (open && event.target instanceof Node && !container.contains(event.target)) close();
  }

  function positionMenu() {
    if (placement !== "top") return;
    const inset = 8;
    const gap = 5;
    const triggerBounds = trigger.getBoundingClientRect();
    const width = Math.max(
      0,
      Math.min(Math.max(150, triggerBounds.width), window.innerWidth - inset * 2)
    );
    const availableHeight = Math.max(40, triggerBounds.top - inset - gap);
    menu.style.width = `${Math.round(width)}px`;
    menu.style.maxHeight = `${Math.min(220, Math.round(availableHeight))}px`;
    const menuBounds = menu.getBoundingClientRect();
    const left = Math.min(
      Math.max(inset, triggerBounds.right - width),
      Math.max(inset, window.innerWidth - width - inset)
    );
    const top = Math.max(inset, triggerBounds.top - menuBounds.height - gap);
    menu.style.left = `${Math.round(left)}px`;
    menu.style.top = `${Math.round(top)}px`;
  }

  function handleWindowResize() {
    if (open) positionMenu();
  }

  trigger.addEventListener("click", handleTriggerClick);
  trigger.addEventListener("keydown", handleTriggerKeydown);
  menu.addEventListener("keydown", handleMenuKeydown);
  select.addEventListener("change", sync);
  document.addEventListener("pointerdown", handleDocumentPointerDown);
  window.addEventListener("resize", handleWindowResize);
  const instance = {
    select,
    trigger,
    menu,
    sync,
    close,
    refresh: rebuildOptions,
    destroy() {
      close();
      document.removeEventListener("pointerdown", handleDocumentPointerDown);
      window.removeEventListener("resize", handleWindowResize);
      select.removeEventListener("change", sync);
      select.hidden = false;
      select.tabIndex = 0;
      select.removeAttribute("aria-hidden");
      container.remove();
      dropdownRegistry.delete(select);
      dropdownInstances.delete(instance);
    }
  };

  rebuildOptions();
  dropdownRegistry.set(select, instance);
  dropdownInstances.add(instance);
  return instance;
}

export function syncChartDropdown(select) {
  dropdownRegistry.get(select)?.sync();
}

export function refreshChartDropdown(select) {
  dropdownRegistry.get(select)?.refresh();
}

function copyAccessibleName(select, trigger) {
  const labelledBy = select.getAttribute("aria-labelledby");
  const label = select.getAttribute("aria-label");
  if (labelledBy) trigger.setAttribute("aria-labelledby", labelledBy);
  else if (label) trigger.setAttribute("aria-label", label);
}

function createChevron() {
  const namespace = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(namespace, "svg");
  const path = document.createElementNS(namespace, "path");
  svg.classList.add("chart-dropdown-chevron");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("aria-hidden", "true");
  path.setAttribute("d", "m6 9 6 6 6-6");
  svg.append(path);
  return svg;
}

function createOptionIcon(name) {
  const namespace = "http://www.w3.org/2000/svg";
  const paths = {
    candles: [
      ["line", { x1: "5", y1: "3", x2: "5", y2: "21" }],
      ["rect", { x: "2.5", y: "7", width: "5", height: "8", rx: "0.75" }],
      ["line", { x1: "13", y1: "2", x2: "13", y2: "18" }],
      ["rect", { x: "10.5", y: "5", width: "5", height: "8", rx: "0.75" }],
      ["line", { x1: "21", y1: "7", x2: "21", y2: "22" }],
      ["rect", { x: "18.5", y: "11", width: "5", height: "7", rx: "0.75" }]
    ],
    bars: [
      ["line", { x1: "5", y1: "4", x2: "5", y2: "20" }],
      ["line", { x1: "2", y1: "9", x2: "5", y2: "9" }],
      ["line", { x1: "5", y1: "15", x2: "8", y2: "15" }],
      ["line", { x1: "13", y1: "2", x2: "13", y2: "18" }],
      ["line", { x1: "10", y1: "6", x2: "13", y2: "6" }],
      ["line", { x1: "13", y1: "12", x2: "16", y2: "12" }],
      ["line", { x1: "21", y1: "7", x2: "21", y2: "22" }],
      ["line", { x1: "18", y1: "12", x2: "21", y2: "12" }],
      ["line", { x1: "21", y1: "18", x2: "24", y2: "18" }]
    ],
    line: [
      ["polyline", { points: "2 18 7 13 11 15 16 7 22 4" }]
    ],
    area: [
      ["path", { d: "M2 18 7 13 11 15 16 7 22 4v16H2Z", class: "chart-dropdown-icon-fill" }],
      ["polyline", { points: "2 18 7 13 11 15 16 7 22 4" }]
    ]
  };
  const shapes = paths[name];
  if (!shapes) return null;

  const svg = document.createElementNS(namespace, "svg");
  svg.classList.add("chart-dropdown-option-icon");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("aria-hidden", "true");
  for (const [tag, attributes] of shapes) {
    const shape = document.createElementNS(namespace, tag);
    for (const [attribute, value] of Object.entries(attributes)) {
      if (attribute === "class") shape.classList.add(value);
      else shape.setAttribute(attribute, value);
    }
    svg.append(shape);
  }
  return svg;
}
